import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Plus, Heart, Trash2, Pencil, Database } from "lucide-react";

function HealthBadge({ status }) {
  if (status === "healthy") return <span className="status-badge ok">sağlıklı</span>;
  if (status === "offline") return <span className="status-badge err">çevrimdışı</span>;
  if (status === "error") return <span className="status-badge err">hata</span>;
  return <span className="status-badge muted">bilinmiyor</span>;
}

export default function PdnsServers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", api_url: "", api_key: "", server_id: "localhost", role: "primary", enabled: true, notes: "" };
  const [form, setForm] = useState(empty);

  const load = async () => {
    try {
      const { data } = await api.get("/pdns");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  const save = async () => {
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.api_key) delete payload.api_key;
        await api.patch(`/pdns/${editing.id}`, payload);
        toast.success("Güncellendi");
      } else {
        await api.post("/pdns", form);
        toast.success("Eklendi");
      }
      setOpen(false);
      setEditing(null);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("PDNS sunucusunu sil?")) return;
    try {
      await api.delete(`/pdns/${id}`);
      toast.success("Silindi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const checkHealth = async (id) => {
    try {
      await api.post(`/pdns/${id}/health`);
      toast.success("Sağlık kontrolü tamamlandı");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, api_url: p.api_url, api_key: "", server_id: p.server_id, role: p.role, enabled: p.enabled, notes: p.notes || "" });
    setOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">PowerDNS Sunucuları</h1>
          <p className="text-sm text-muted-foreground mt-1">Hedef PDNS node'ları · REST API üzerinden sağlık ve zone sayısı</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="pdns-add-btn">
          <Plus size={14} className="mr-1.5" /> Yeni PDNS
        </Button>
      </div>

      <div className="border border-border bg-card rounded-md overflow-hidden">
        <table className="data-table" data-testid="pdns-table">
          <thead>
            <tr>
              <th>Ad</th>
              <th>Rol</th>
              <th>Durum</th>
              <th>Versiyon</th>
              <th>Zone</th>
              <th>Son Kontrol</th>
              <th className="text-right">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-muted-foreground text-sm font-mono">PowerDNS sunucusu eklenmemiş</td></tr>
            )}
            {rows.map((p) => (
              <tr key={p.id} data-testid={`pdns-row-${p.id}`}>
                <td>
                  <div className="font-medium flex items-center gap-2">
                    <Database size={14} className="text-muted-foreground" />{p.name}
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground/70 mt-0.5">{p.api_url.replace(/^https?:\/\//, "")}</div>
                </td>
                <td><span className="status-badge info">{p.role}</span></td>
                <td>
                  <div className="flex flex-col gap-1">
                    <HealthBadge status={p.health_status} />
                    {p.health_message && <div className="text-[10px] text-red-400/70 font-mono truncate max-w-[220px]" title={p.health_message}>{p.health_message}</div>}
                  </div>
                </td>
                <td className="font-mono text-xs">{p.version || "—"}</td>
                <td className="font-mono text-xs">{p.zone_count}</td>
                <td className="font-mono text-xs text-muted-foreground">{p.last_health_check ? new Date(p.last_health_check).toLocaleString("tr-TR") : "—"}</td>
                <td>
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => checkHealth(p.id)} data-testid={`pdns-health-${p.id}`}><Heart size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`pdns-edit-${p.id}`}><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(p.id)} data-testid={`pdns-delete-${p.id}`} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="pdns-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "PDNS Düzenle" : "Yeni PowerDNS Sunucusu"}</DialogTitle>
            <DialogDescription>PowerDNS REST API bilgileri</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Ad</Label>
              <Input data-testid="pdns-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="pdns-01" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">API URL</Label>
              <Input data-testid="pdns-form-url" value={form.api_url} onChange={(e) => setForm({ ...form, api_url: e.target.value })} placeholder="https://ns1.netlen.com.tr:8081" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">API Key</Label>
                <Input type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder={editing ? "(değiştirmemek için boş)" : ""} />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Server ID</Label>
                <Input value={form.server_id} onChange={(e) => setForm({ ...form, server_id: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="pdns-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="primary">primary</SelectItem>
                  <SelectItem value="secondary">secondary</SelectItem>
                  <SelectItem value="anycast">anycast</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Notlar</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
              <span className="text-sm">Etkin (senkron hedefi)</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button onClick={save} data-testid="pdns-form-save">{editing ? "Kaydet" : "Ekle"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
